/**
 * **The domain card** — one card, on onboarding's Domain step and on Website →
 * Domain (FR-A3, FR-A9; [ADR-0051](../../../../../docs/adr/0051-the-domain-is-cores-and-prodesk-only-adopts-one.md)).
 *
 * ADR-0051 §3: *"Your hostname and `DomainStep` merge into one card"*, and its
 * Consequences: *"The onboarding `DomainStep` is the same component and follows
 * it."* Until C27 it did not — onboarding still had a **Hostname** box and
 * *Check this hostname* beside this card, posting `custom_domain` through
 * `POST /onboarding`, a door `PATCH /profile` had already closed. UAT report
 * #7 found it. Now both screens draw this, and nothing on either is typed.
 *
 * **No field and no Save.** The card lists what core says is bound to this
 * firm and the firm picks:
 *
 * - **exactly one bound host** → adopted on the first read that finds it, and
 *   shown with its state, its DNS detail and *Check again*;
 * - **several** → a radio list. Picking saves; there is no separate Save;
 * - **none** → said plainly, with everything-else-works and a link to the
 *   console — and any host core holds that is **not live yet** is shown with
 *   its state and the DNS records to publish, because that is what a firm half
 *   way through binding one came here for;
 * - **a host that was bound and is now revoked** → the claim is *not* silently
 *   dropped. Dropping one on a transient read failure would unpublish a firm's
 *   website, so it is cleared only by a successful read that does not contain
 *   it — and then the firm is told, not surprised.
 *
 * ## The reads, and which is the gate
 *
 * - **Our own service** (`/api/v1/firm/domain`) is the **gate** — live,
 *   fail-closed, and `can_publish` is what the website and the portal act on
 *   (ADR-0008 rule 4).
 * - **Core's own contract** (`/api/domains/api/v1/domains`) is the **list and
 *   the detail** — user-scoped and browser-facing, so this card reads it with
 *   the signed-in session rather than asking our service to impersonate
 *   anyone. That is also why adoption is driven from here: the service cannot
 *   list a firm's hosts, only check one (`firm.adopt_domain`), and it checks
 *   the one it is given against core before storing it.
 *
 * ## Three states for core's read, not two (UAT report #7, C27)
 *
 * The card used to hold core's answer as `CoreDomain[] | null`, with `null`
 * meaning both *not read yet* and *could not be read*. So the first render —
 * before either request had answered — said *"We couldn't read the DNS
 * records…"*, and a `Promise.all` threw core's good answer away whenever our
 * own read failed beside it. Now: **not read yet** draws a skeleton, **read**
 * draws the list, and the couldn't-read sentence appears only after core's
 * read has actually failed. The two reads settle independently
 * (`Promise.allSettled`), so neither can hide the other.
 *
 * Nothing here binds a domain. Binding is the platform console's, and ProDesk
 * sells no domains at all — spec §9-12.
 */

import { Alert, Button, Card, CardBody, Skeleton } from '@fiveox/ui'
import { ExternalLink, Globe, RefreshCw } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useState } from 'react'

import { CONSOLE_URL } from '../../lib/config'
import { ApiError } from '../../lib/api'
import {
  type CoreDnsRecord,
  type CoreDomain,
  type DomainBinding,
  coreDomains,
  domainCopy,
  firm,
} from '../../lib/api/firm'

/** Core's words for its five states. */
export const CORE_STATE_LABELS: Record<string, string> = {
  pending: 'Waiting for DNS',
  verified: 'Verified — not serving yet',
  failed: 'Last check failed',
  bound: 'Live',
  revoked: 'Revoked',
}

/**
 * **Which host ProDesk should adopt, given core's list and what is stored.**
 *
 * Pure, so the rule is testable without a screen:
 *
 * - one bound host and no claim → adopt it (the common case, and the whole
 *   reason the firm never has to type anything);
 * - one bound host that *is* the claim → nothing to do;
 * - several → `null`: the firm picks, because ProDesk guessing which of a
 *   firm's two domains its website serves on is a guess with a visible
 *   consequence;
 * - a claim that core's list does not contain → `null`, and the card says
 *   so. **Never a silent clear**: see the file header.
 */
export function hostToAdopt(bound: string[], claimed: string | null): string | null {
  if (bound.length === 1 && bound[0] !== claimed) return bound[0] ?? null
  return null
}

/** Whether the stored claim has gone from core's bound list. */
export function claimIsStale(bound: string[], claimed: string | null): boolean {
  return Boolean(claimed) && bound.length > 0 && !bound.includes(claimed as string)
}

/** What one *Check again* found. `core` is `null` when core's read failed —
 *  never when it is merely empty. */
interface DomainRead {
  binding: DomainBinding | null
  core: CoreDomain[] | null
}

/** Both reads, each settling on its own (C27, UAT report #7). */
export async function readDomains(): Promise<DomainRead> {
  const [ours, theirs] = await Promise.allSettled([firm.domain(), coreDomains()])
  return {
    binding: ours.status === 'fulfilled' ? ours.value.data : null,
    // `coreDomains()` already answers `null` for an unreachable core, a
    // refused origin or no org — that is the one *failed*; `[]` is *none*.
    core: theirs.status === 'fulfilled' ? theirs.value : null,
  }
}

export function DomainStep({
  editable,
  heading = true,
}: {
  /** `settings:can_update` — whether the firm may pick which host is used. */
  editable: boolean
  /** The card's own title. Onboarding's step already says *Your domain*. */
  heading?: boolean
}) {
  const [binding, setBinding] = useState<DomainBinding | null>(null)
  /** `null` until core has answered once. */
  const [fromCore, setFromCore] = useState<CoreDomain[] | null>(null)
  /** Whether core's **last** read failed — the only thing that may draw the
   *  couldn't-read sentence. */
  const [coreFailed, setCoreFailed] = useState(false)
  const [checking, setChecking] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const check = useCallback(async (): Promise<DomainRead> => {
    setChecking(true)
    try {
      // Both, every time. The gate read is uncached on the service side, so
      // this *is* the re-check — there is nothing stored to invalidate.
      const read = await readDomains()
      // A failed gate read keeps the last good answer: it is not a screen the
      // firm can act on, and *Check again* is right there.
      if (read.binding) setBinding(read.binding)
      // A failed core read keeps the last good list too, and says so.
      if (read.core) setFromCore(read.core)
      setCoreFailed(read.core === null)
      return read
    } finally {
      setChecking(false)
    }
  }, [])

  const adopt = useCallback(
    async (host: string | null) => {
      setBusy(host ?? 'none')
      setError(null)
      setNotice(null)
      try {
        const body = await firm.adoptDomain(host)
        setBinding(body.data)
        setNotice(
          host
            ? `Your website and client portal will use ${host}.`
            : 'ProDesk is no longer using a domain of yours.',
        )
        await check()
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That domain could not be used.')
      } finally {
        setBusy(null)
      }
    },
    [check],
  )

  // The first read, and the silent adoption that follows it where there is
  // exactly one bound host and nothing claimed. **Only when both reads
  // answered**: adopting on core's word alone, with no idea what is claimed,
  // would be a guess.
  useEffect(() => {
    let cancelled = false
    void check().then((read) => {
      if (cancelled || !editable || !read.binding || !read.core) return
      const bound = read.core.filter((d) => d.state === 'bound').map((d) => d.hostname)
      const only = hostToAdopt(bound, read.binding.host)
      if (only) void adopt(only)
    })
    return () => {
      cancelled = true
    }
    // Once, on arrival. `check` and `adopt` are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const bound = (fromCore ?? []).filter((d) => d.state === 'bound')
  const notLive = (fromCore ?? []).filter((d) => d.state !== 'bound' && d.state !== 'revoked')
  const boundHosts = bound.map((d) => d.hostname)
  const claimed = binding?.host ?? null
  const chosen = (fromCore ?? []).find((d) => d.hostname === claimed) ?? null
  const stale = fromCore !== null && claimIsStale(boundHosts, claimed)
  const copy = domainCopy(binding?.state ?? 'no_domain')

  let body: ReactNode
  if (fromCore === null && !coreFailed) {
    // Not read yet. A skeleton, and never the couldn't-read sentence.
    body = (
      <div aria-busy="true" data-domain-loading="">
        <Skeleton className="h-28 w-full rounded-xl" />
      </div>
    )
  } else if (fromCore === null) {
    // Core could not be read at all. What our own service says is still the
    // real state, so it is drawn — but nothing is claimed about core's list.
    body = (
      <div className="space-y-1" data-domain-unread="">
        {claimed && <p className="text-foreground text-base font-medium">{claimed}</p>}
        <p className="text-muted-foreground text-sm">{copy.body}</p>
      </div>
    )
  } else if (boundHosts.length === 0) {
    body = (
      <div className="space-y-3" data-domain-none="">
        <div className="space-y-2">
          <p className="text-foreground text-sm font-medium">No domain bound yet.</p>
          <p className="text-muted-foreground text-sm">
            Everything else in ProDesk works without one — clients, cases, billing and the builder.
            Your website and client portal need one before anyone outside the firm can reach them.
          </p>
        </div>
        {notLive.map((row) => (
          <div key={row.hostname} className="space-y-2" data-domain-pending={row.hostname}>
            <Alert tone={row.state === 'failed' ? 'warning' : 'info'}>
              <span className="font-medium">{row.hostname}</span> —{' '}
              {CORE_STATE_LABELS[row.state] ?? row.state}
              {row.last_error ? ` — ${row.last_error}` : ''}
            </Alert>
            <DnsRecords records={row.instructions?.records ?? []} />
          </div>
        ))}
      </div>
    )
  } else if (boundHosts.length === 1) {
    body = (
      <div className="space-y-1" data-domain-one={boundHosts[0]}>
        <p className="text-foreground text-base font-medium">{boundHosts[0]}</p>
        <p className="text-muted-foreground text-sm">
          {claimed === boundHosts[0] ? copy.body : editable ? 'Being used now…' : copy.body}
        </p>
      </div>
    )
  } else {
    body = (
      <fieldset className="space-y-2" data-domain-pick="">
        <legend className="text-foreground text-sm font-medium">
          You have more than one. Which should your website use?
        </legend>
        {bound.map((row) => (
          <label
            key={row.hostname}
            data-domain-option={row.hostname}
            className="border-border flex cursor-pointer items-start gap-2 rounded-lg border p-3"
          >
            <input
              type="radio"
              name="domain"
              className="mt-1"
              checked={claimed === row.hostname}
              disabled={!editable || busy !== null}
              onChange={() => void adopt(row.hostname)}
            />
            <span className="min-w-0">
              <span className="text-foreground block text-sm font-medium">{row.hostname}</span>
              <span className="text-muted-foreground block text-xs">
                {CORE_STATE_LABELS[row.state] ?? row.state}
              </span>
            </span>
          </label>
        ))}
      </fieldset>
    )
  }

  return (
    <>
      {error && <Alert tone="error">{error}</Alert>}
      {notice && !error && <Alert tone="success">{notice}</Alert>}

      <Card data-domain-card="">
        <CardBody className="space-y-4">
          {heading && (
            <div className="flex items-start gap-2">
              <Globe size={18} className="mt-0.5 shrink-0 text-primary" aria-hidden />
              <div className="min-w-0">
                <h2 className="text-foreground text-base font-semibold">Your domain</h2>
                <p className="text-muted-foreground text-sm">
                  Domains are bound in the platform console. ProDesk watches what you bind and tells
                  you when it is live.
                </p>
              </div>
            </div>
          )}

          {body}

          {coreFailed && (
            // Degraded, and said plainly — and only once core's read has
            // actually failed. The state above is still the real one: it
            // comes from our own service, not from this read.
            <p className="text-muted-foreground text-sm" data-domain-core-failed="">
              We couldn’t read the DNS records from the platform just now. The console shows them.
            </p>
          )}

          {stale && (
            <Alert tone="warning" data-domain-stale="">
              <span className="font-medium">
                {claimed} is no longer bound to your firm in the platform console.
              </span>{' '}
              Your website and client portal are not being served on it. Pick one of the domains
              above, or bind it again in the console.
            </Alert>
          )}

          {chosen?.last_error && (
            <Alert tone="warning">
              The last check on {chosen.hostname} failed: {chosen.last_error}
            </Alert>
          )}

          {chosen && <DnsRecords records={chosen.instructions?.records ?? []} />}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              loading={checking}
              loadingText="Checking…"
              onClick={() => void check()}
            >
              <RefreshCw size={14} aria-hidden /> Check again
            </Button>
            <a
              className="text-primary inline-flex items-center gap-1 self-center text-sm hover:underline"
              href={`${CONSOLE_URL}/domains`}
              target="_blank"
              rel="noreferrer"
            >
              {boundHosts.length > 0 || notLive.length > 0
                ? 'Manage in the platform console'
                : 'Add your domain in the platform console'}{' '}
              <ExternalLink size={12} aria-hidden />
            </a>
            {claimed && editable && boundHosts.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                disabled={busy !== null}
                onClick={() => void adopt(null)}
              >
                Stop using it
              </Button>
            )}
          </div>

          {!editable && (
            <p className="text-muted-foreground text-xs">
              You can see the domain but not change which one is used.
            </p>
          )}
        </CardBody>
      </Card>
    </>
  )
}

/** The records core prescribes, verbatim. Scrollable rather than wrapped: a
 *  record a reader has to reassemble from two lines is a record they will
 *  mistype. */
function DnsRecords({ records }: { records: CoreDnsRecord[] }) {
  if (records.length === 0) return null
  return (
    <div data-dns-records="">
      <p className="mb-2 text-sm font-medium text-foreground">
        Publish these records at your DNS provider
      </p>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Purpose</th>
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 font-medium">Value</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record, index) => (
              <tr key={String(record.purpose ?? index)} className="border-b border-border/50">
                <td className="px-3 py-2">{String(record.purpose ?? '')}</td>
                <td className="px-3 py-2 font-mono text-xs">{String(record.type ?? '')}</td>
                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{String(record.name ?? '')}</td>
                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{String(record.value ?? '')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
